import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import toast from 'react-hot-toast';

const BANKS = [
  '국민은행', '신한은행', '우리은행', '하나은행', 'NH농협은행',
  'IBK기업은행', 'SC제일은행', '씨티은행', '카카오뱅크', '토스뱅크',
  '케이뱅크', '새마을금고', '신협', '우체국', '수협은행',
];

type AccountType = 'personal' | 'business';
type RewardTab = 'settlement' | 'payment';

interface WithdrawalRecord {
  withdrawalId: string;
  amount: number;
  bankName: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  status: string;
  requestDate: string;
  expectedDate: string;
  completedDate: string;
}

const SettlementPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [accountType, setAccountType] = useState<AccountType>('personal');
  const [bank, setBank] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [rewardTab, setRewardTab] = useState<RewardTab>('settlement');
  const [influencerId, setInfluencerId] = useState<string | null>(null);
  const [hasBankAccount, setHasBankAccount] = useState(false);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [availableReward, setAvailableReward] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId) { setLoading(false); return; }
      try {
        // Fetch bank account from influencer profile
        const infRes = await graphqlApi.getInfluencerByUserId(user.userId);
        if (infRes.success && infRes.data) {
          const inf = infRes.data;
          setInfluencerId(inf.influencerId);
          if (inf.bankAccountType) setAccountType(inf.bankAccountType as AccountType);
          if (inf.bankName) { setBank(inf.bankName); setHasBankAccount(true); }
          if (inf.bankAccountHolder) setAccountHolder(inf.bankAccountHolder);
          if (inf.bankAccountNumber) setAccountNumber(inf.bankAccountNumber);
          setAvailableReward(inf.rewardBalance || 0);
        }
        // Fetch withdrawal records
        const wRes = await graphqlApi.getWithdrawals(user.userId);
        if (wRes.success && wRes.data?.withdrawals) {
          setWithdrawals(wRes.data.withdrawals);
        }
      } catch (err) {
        console.error('Failed to fetch settlement data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.userId]);

  const [showRegisterAlert, setShowRegisterAlert] = useState(false);

  const handleRegister = () => {
    setShowRegisterAlert(true);
  };

  const handleRequestWithdrawal = async () => {
    if (!user?.userId) return;
    if (!hasBankAccount) {
      toast.error(t('myProfile.settlement.registerFirst'));
      return;
    }
    setRequesting(true);
    try {
      const res = await graphqlApi.requestWithdrawal(user.userId, availableReward);
      if (res.success && res.data) {
        setWithdrawals(prev => [res.data, ...prev]);
        setAvailableReward(prev => prev - availableReward);
        toast.success(t('myProfile.settlement.withdrawalRequested'));
      } else {
        toast.error(res.error || 'Failed');
      }
    } catch { toast.error('Failed to request withdrawal'); }
    finally { setRequesting(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <>
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
      {/* Page Title */}
      <div className="pt-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('myProfile.sidebar.settlement')}</h2>
        <hr className="border-gray-200" />
      </div>

      {/* 계좌정보 */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('myProfile.settlement.accountInfo')}</h2>
        <hr className="border-gray-200 mb-4" />
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="accountType" checked={accountType === 'personal'} onChange={() => setAccountType('personal')} className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-gray-700">{t('myProfile.settlement.personalAccount')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="accountType" checked={accountType === 'business'} onChange={() => setAccountType('business')} className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-gray-700">{t('myProfile.settlement.businessAccount')}</span>
          </label>
        </div>
        <select value={bank} onChange={(e) => setBank(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3 focus:ring-1 focus:outline-none focus:ring-blue-500">
          <option value="">{t('myProfile.settlement.selectBank')}</option>
          {BANKS.map((b) => (<option key={b} value={b}>{b}</option>))}
        </select>
        <div className="flex gap-2 mb-3">
          <input type="text" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} placeholder={t('myProfile.settlement.accountHolderPlaceholder')} className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
          <input type="text" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder={t('myProfile.settlement.accountNumberPlaceholder')} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
          <button type="button" onClick={handleRegister} disabled={!bank || !accountHolder || !accountNumber} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
            {t('myProfile.settlement.register')}
          </button>
        </div>
        <p className="text-xs text-gray-400">{t('myProfile.settlement.accountNotice')}</p>
      </section>

      {/* 나의 리워드 */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('myProfile.settlement.myReward')}</h2>
        <hr className="border-gray-200 mb-4" />
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="text-sm text-gray-600">{t('myProfile.settlement.availableReward')}</span>
            <span className="ml-3 text-xl font-bold text-blue-600">{availableReward.toLocaleString()}</span>
          </div>
          <button type="button" onClick={handleRequestWithdrawal} disabled={requesting || availableReward <= 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {requesting ? '...' : t('myProfile.settlement.requestWithdrawal')}
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setRewardTab('settlement')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${rewardTab === 'settlement' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t('myProfile.settlement.settlementTab')}
          </button>
          <button onClick={() => setRewardTab('payment')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${rewardTab === 'payment' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t('myProfile.settlement.paymentTab')}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.requestDate')}</th>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.bankAccount')}</th>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.amount')}</th>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.status')}</th>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.expectedDate')}</th>
                <th className="px-3 py-2 font-medium">{t('myProfile.settlement.table.completedDate')}</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filtered = rewardTab === 'payment'
                  ? withdrawals.filter(r => r.status === 'completed')
                  : withdrawals;
                return filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">{t('myProfile.settlement.noRecords', 'No records')}</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.withdrawalId} className="border-b border-gray-100">
                  <td className="px-3 py-3">{r.requestDate}</td>
                  <td className="px-3 py-3">{r.bankName} {r.bankAccountNumber}</td>
                  <td className="px-3 py-3">{r.amount.toLocaleString()}원</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      r.status === 'requested' ? 'bg-yellow-100 text-yellow-700' :
                      r.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{r.status === 'requested' ? '신청중' : r.status === 'completed' ? '입금완료' : r.status}</span>
                  </td>
                  <td className="px-3 py-3">{r.expectedDate || '-'}</td>
                  <td className="px-3 py-3">{r.completedDate || '-'}</td>
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </div>
      </section>
    </div>

    {/* Register Alert Modal */}
    {showRegisterAlert && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRegisterAlert(false)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-bold text-gray-900">{t('myProfile.settlement.registerAlertTitle', '계좌 등록 안내')}</h3>
          <hr className="border-gray-200" />
          <p className="text-sm text-gray-600 leading-relaxed">{t('myProfile.settlement.registerAlertMessage', '리워드 정산은 수익 발생이 확정된 후 시작됩니다. 등록이 필요한 시점에 맞춰 알림이 발송됩니다.')}</p>
          <button onClick={() => setShowRegisterAlert(false)} className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">
            {t('common:confirm', '확인')}
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default SettlementPage;
